import { useMemo } from 'react'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import useGroupedLoyaltyEarnTotals, {
  type GroupedLoyaltyEarnCalculationRequest,
  type LoyaltyEarnTotals,
} from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import {
  generateLoyaltyPointsCalculatorBedbankOptions,
  generateLoyaltyPointsCalculatorBundledFlightOptions,
  generateLoyaltyPointsCalculatorHotelOptions,
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { isLEHotel } from 'lib/offer/offerTypes'
import type { PropertyRoomRateViewForAssistant } from './offerPageContextTypes'
import { getBedbankMemberTotalIncludingPropertyFees } from './offerPageBedbankPricing'

interface UseOfferPageRoomPackageLoyaltyEarnTotalsParams {
  offerPageState: OfferPageState
  currentRegionCode: string
  hotelPackageViews: Array<App.OfferPackageOptionView>
  bedbankPackageViews: Array<App.BedbankOfferPackageView>
  propertyRoomRateViews: Array<PropertyRoomRateViewForAssistant>
  propertyIsUltraLux: boolean
}

// Rate/package identity, not array position -- a room can have a variable number of rates, so a
// stable string key (rather than an index into some particular array) is what lets
// `buildOfferPageSnapshot.ts` look up the right entry for a specific rate without needing to
// independently re-derive this file's exact iteration order.
type OfferPageRoomPackageLoyaltyGroupKey = string
type OfferPageRoomPackageLoyaltyCalculationRequest = GroupedLoyaltyEarnCalculationRequest<OfferPageRoomPackageLoyaltyGroupKey>

function addCalculationRequest(
  calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest>,
  groupKey: OfferPageRoomPackageLoyaltyGroupKey,
  request: App.LoyaltyPointsEarnCalculationRequest | undefined,
) {
  if (!request) {
    return
  }

  calculationRequests.push({
    groupKey,
    request,
  })
}

function buildPropertyLoyaltyCalculationRequests({
  offerPageState,
  currentRegionCode,
  propertyRoomRateViews,
  propertyIsUltraLux,
}: Pick<UseOfferPageRoomPackageLoyaltyEarnTotalsParams, 'offerPageState' | 'currentRegionCode' | 'propertyRoomRateViews' | 'propertyIsUltraLux'>): Array<OfferPageRoomPackageLoyaltyCalculationRequest> {
  const property = offerPageState.property
  if (!property || propertyRoomRateViews.length === 0) {
    return []
  }

  const calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest> = []
  propertyRoomRateViews.forEach((roomRateView) => {
    roomRateView.rates.forEach((rate) => {
      addCalculationRequest(
        calculationRequests,
        rate.rateId,
        generateLoyaltyPointsCalculatorPropertyOptions(rate, {
          propertyCountryCode: property.location.countryCode,
          propertyState: property.location.region,
          currentRegionCode,
          isUltraLux: propertyIsUltraLux,
          nights: offerPageState.duration,
        }),
      )
      addCalculationRequest(
        calculationRequests,
        rate.rateId,
        generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
      )
    })
  })

  return calculationRequests
}

function buildHotelLoyaltyCalculationRequests({
  offerPageState,
  currentRegionCode,
  hotelPackageViews,
}: Pick<UseOfferPageRoomPackageLoyaltyEarnTotalsParams, 'offerPageState' | 'currentRegionCode' | 'hotelPackageViews'>): Array<OfferPageRoomPackageLoyaltyCalculationRequest> {
  const offer = offerPageState.offer
  if (!isLEHotel(offer) || hotelPackageViews.length === 0) {
    return []
  }

  const calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest> = []
  hotelPackageViews.forEach((packageView) => {
    const groupKey = packageView.package.uniqueKey
    addCalculationRequest(
      calculationRequests,
      groupKey,
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        packageView,
        currentRegionCode,
        nights: packageView.package.duration,
      }),
    )
    addCalculationRequest(
      calculationRequests,
      groupKey,
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        packageView,
        flightPrice: packageView.flightPrice > 0 ? packageView.flightPrice : undefined,
        skip: packageView.flightPrice <= 0,
      }),
    )
  })

  return calculationRequests
}

function buildBedbankLoyaltyCalculationRequests({
  offerPageState,
  currentRegionCode,
  bedbankPackageViews,
}: Pick<UseOfferPageRoomPackageLoyaltyEarnTotalsParams, 'offerPageState' | 'currentRegionCode' | 'bedbankPackageViews'>): Array<OfferPageRoomPackageLoyaltyCalculationRequest> {
  const offer = offerPageState.bedbankOffer
  if (!offer || bedbankPackageViews.length === 0) {
    return []
  }

  const calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest> = []
  bedbankPackageViews.forEach((packageView) => {
    // `effectiveRates`, not `rates` -- must match the snapshot builder's own rate set (and rate
    // IDs) exactly, since `buildOfferPageSnapshot.ts` looks up these totals by rate ID.
    packageView.effectiveRates.forEach((rate) => {
      addCalculationRequest(
        calculationRequests,
        rate.id,
        generateLoyaltyPointsCalculatorBedbankOptions(rate, {
          price: rate.totals.total,
          memberPrice: getBedbankMemberTotalIncludingPropertyFees(rate),
          propertyFees: rate.totals.propertyFees ?? 0,
          propertyId: offer.property.id,
          offerId: offer.id,
          propertyCountryCode: offer.property.address.countryCode,
          propertyState: offer.property.address.stateProvinceCode,
          currentRegionCode,
          nights: rate.nights,
        }),
      )
    })
  })

  return calculationRequests
}

function buildCalculationRequests(
  params: UseOfferPageRoomPackageLoyaltyEarnTotalsParams,
): {
  groupKeys: Array<OfferPageRoomPackageLoyaltyGroupKey>
  calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest>
} {
  if (params.propertyRoomRateViews.length > 0) {
    return {
      groupKeys: params.propertyRoomRateViews.flatMap(roomRateView => roomRateView.rates.map(rate => rate.rateId)),
      calculationRequests: buildPropertyLoyaltyCalculationRequests(params),
    }
  }

  if (params.hotelPackageViews.length > 0) {
    return {
      groupKeys: params.hotelPackageViews.map(packageView => packageView.package.uniqueKey),
      calculationRequests: buildHotelLoyaltyCalculationRequests(params),
    }
  }

  return {
    groupKeys: params.bedbankPackageViews.flatMap(packageView => packageView.effectiveRates.map(rate => rate.id)),
    calculationRequests: buildBedbankLoyaltyCalculationRequests(params),
  }
}

export type OfferPageLoyaltyEarnCalculationStatus = 'loading' | 'partial' | 'unavailable' | 'ready'

/**
 * Aggregate status across every calculation in the batch, mirroring `ratesStatusFact`'s
 * loading/unavailable-or-nothing shape: `undefined` when nothing has been requested yet (no fact
 * to report), `'loading'` while any calculation is still in flight, `'unavailable'` once settled
 * with every calculation errored, `'partial'` once settled with a mix of data and errors, and
 * `'ready'` once settled with no errors at all. Computed here (not in `useGroupedLoyaltyEarnTotals`,
 * which is `src/loyalty`-owned) from a second, tripPlanner-owned read of the same underlying
 * calculations -- see the `pointsCalculations` hook call below for why this doesn't duplicate work.
 */
export function getOfferPageLoyaltyEarnCalculationStatus(
  pointsCalculations: Array<App.StatefulData<App.LoyaltyPointsEarnCalculation> | undefined>,
): OfferPageLoyaltyEarnCalculationStatus | undefined {
  if (pointsCalculations.length === 0) {
    return undefined
  }

  if (pointsCalculations.some(calculation => !calculation || calculation.initial || calculation.fetching)) {
    return 'loading'
  }

  const hasError = pointsCalculations.some(calculation => calculation?.error !== undefined)
  if (!hasError) {
    return 'ready'
  }

  const hasData = pointsCalculations.some(calculation => calculation?.data !== undefined)
  return hasData ? 'partial' : 'unavailable'
}

/**
 * `useGroupedLoyaltyEarnTotals` (owned by `src/loyalty`) returns its totals as a plain array,
 * positionally aligned with the `groupKeys` array passed in -- deliberately left untouched here
 * rather than changed to a keyed return shape, since it's shared/loyalty-owned code. This zips
 * that array with the same `groupKeys` locally so callers in this tripPlanner-owned module can
 * look totals up by stable rate/package key instead of by array position.
 */
function zipLoyaltyEarnTotalsByGroupKey(
  groupKeys: Array<OfferPageRoomPackageLoyaltyGroupKey>,
  totals: Array<LoyaltyEarnTotals | undefined>,
): Record<string, LoyaltyEarnTotals | undefined> {
  const result: Record<string, LoyaltyEarnTotals | undefined> = {}
  groupKeys.forEach((groupKey, index) => {
    result[groupKey] = totals[index]
  })
  return result
}

export interface OfferPageRoomPackageLoyaltyEarnTotalsResult {
  totalsByGroupKey: Record<string, LoyaltyEarnTotals | undefined>
  status: OfferPageLoyaltyEarnCalculationStatus | undefined
}

export default function useOfferPageRoomPackageLoyaltyEarnTotals(
  params: UseOfferPageRoomPackageLoyaltyEarnTotalsParams,
): OfferPageRoomPackageLoyaltyEarnTotalsResult {
  const {
    offerPageState,
    currentRegionCode,
    hotelPackageViews,
    bedbankPackageViews,
    propertyRoomRateViews,
    propertyIsUltraLux,
  } = params
  const {
    groupKeys,
    calculationRequests,
  } = useMemo(() => buildCalculationRequests({
    offerPageState,
    currentRegionCode,
    hotelPackageViews,
    bedbankPackageViews,
    propertyRoomRateViews,
    propertyIsUltraLux,
  }), [
    bedbankPackageViews,
    currentRegionCode,
    hotelPackageViews,
    offerPageState,
    propertyIsUltraLux,
    propertyRoomRateViews,
  ])

  const totals = useGroupedLoyaltyEarnTotals(groupKeys, calculationRequests)
  // A second read of the same underlying calculations, purely to derive an aggregate status --
  // deliberately not a second *request*: `submitLoyaltyPointsEarnCalculationBatchRequest` dedupes
  // by key against whatever's already `fetching`/settled in the store, and this call passes the
  // exact same request array `useGroupedLoyaltyEarnTotals` above already dispatched, so it hits
  // that same in-flight/cached state rather than firing a second API call.
  const requestsOnly = useMemo(() => calculationRequests.map(requestInfo => requestInfo.request), [calculationRequests])
  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(requestsOnly, {
    skipAll: requestsOnly.length === 0,
    useBatch: true,
  })
  const status = useMemo(() => getOfferPageLoyaltyEarnCalculationStatus(pointsCalculations), [pointsCalculations])

  return useMemo(
    () => ({
      totalsByGroupKey: zipLoyaltyEarnTotalsByGroupKey(groupKeys, totals),
      status,
    }),
    [groupKeys, totals, status],
  )
}
